// src/app/user/services/ai-assistant.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { apiUrl } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';

/**
 * ASK AI — assistant ka context.
 *
 * This service does three things:
 *   1. Asks `/context` once and keeps the answer for everybody (the FAB and the
 *      page both read it — two calls would make no sense).
 *   2. Works out from the URL WHICH project the admin is standing on, so the
 *      assistant knows without asking.
 *   3. Refreshes the context on route changes — because if the project changed,
 *      so did "what is possible right now".
 *
 * ⚠️ There is no model call here. Only state.
 */

export interface AiQuestion {
  key: string;
  text: string;
  why?: string;
  options: { value: string; label: string }[];
}

/** What the model suggested — but NOTHING has happened yet. */
export interface AiProposal {
  action: string;
  plan?: any;
  capability?: { key: string; label: string; writes: string[] };
  error?: string;
  msg?: string;
  next?: string;
  question?: string;
}

export interface AiTurn {
  who: 'you' | 'ai';
  text: string;
  proposal?: AiProposal | null;
  question?: AiQuestion | null;
  /** The outcome after confirmation — so the transcript shows WHAT happened. */
  applied?: string | null;
  pending?: boolean;
}

export interface AiActionState { state: 'ready' | 'blocked' | 'done'; why: string | null; next: string | null; }

export interface AiContext {
  allowed: boolean;
  code?: string;
  msg?: string;
  can_accept?: boolean;
  provider?: string | null;
  region?: string | null;
  quota?: any;
  org?: any;
  project?: any;
  actions?: { [k: string]: AiActionState };
  /** What can be done right now. Not hardcoded — derived from the server state. */
  chips?: string[];
  /** When nothing is ready — the steps that clear the blockage. */
  unblock?: string[];
  diagnosis?: { code: string; msg: string }[];
  sessions?: any[];
  /**
   * Each action's name, one-line meaning, and which table it writes to — from
   * the server. The client keeps NO label list of its own: names in two places
   * differ one day, and adding a new domain (HR, Tasks) should not mean
   * touching the client at all.
   */
  meta?: { [k: string]: { label: string; desc: string; writes: string[]; domain: string | null } };
}

@Injectable({ providedIn: 'root' })
export class AiAssistantService {
  private apiLink = apiUrl + '/api/ai-assistant/';

  /** The last known context. The FAB hides or shows itself from this. */
  readonly context$ = new BehaviorSubject<AiContext | null>(null);

  private inFlight: Promise<AiContext | null> | null = null;
  private lastProjectId: number | null = null;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router,
  ) {
    // The context changes when the project does — so on every navigation, check
    // whether the project id has changed.
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      const pid = this.currentProjectId();
      if (pid !== this.lastProjectId) {
        this.lastProjectId = pid;
        this.load(true);
      }
    });
  }

  private async authHeaders(): Promise<HttpHeaders> {
    const d = await this.authService.getUserDetails();
    return new HttpHeaders({
      Authorization: `Bearer ${d?.token || ''}`,
      'Content-Type': 'application/json',
    });
  }

  /**
   * URL se project id.
   *
   * Three shapes are in use in this app, and all three have to be handled:
   *   /user/project/logframe-me/3450          → aakhri numeric segment
   *   /user/project/ai-design/3450            → wahi
   *   /user/project/viewproject?project=MzQ1MA==   → base64 query
   */
  currentProjectId(): number | null {
    const url = this.router.url || '';
    const q = url.split('?')[1] || '';

    // 1 · Query param — an EXPLICIT marker, so it is accepted on any URL.
    //     Two shapes are in use in this app:
    //        ?project=3450        (the assistant writes this itself)
    //        ?project=MzQ1MA==    (manage.component sends toBase64(id))
    const m = q.match(/(?:^|&)project(?:_id)?=([^&]+)/);
    if (m) {
      const raw = decodeURIComponent(m[1]);
      const plain = parseInt(raw, 10);
      if (Number.isFinite(plain) && plain > 0 && String(plain) === raw) return plain;
      try {
        const id = parseInt(atob(raw), 10);
        if (Number.isFinite(id) && id > 0) return id;
      } catch { /* it was not base64 */ }
    }

    // 2 · A number at the end of the URL — but ONLY on the routes that really
    //     carry a project id. Without that guard `/user/employee/payslip/12`
    //     would be read as project 12, and the assistant would quietly pick up
    //     the wrong project's context — worse than giving a plainly wrong answer.
    const path = url.split('?')[0];
    if (path.indexOf('/project') < 0 && path.indexOf('logframe') < 0) return null;

    const seg = path.split('/').filter(Boolean);
    const last = seg[seg.length - 1];
    const id = parseInt(last, 10);
    return Number.isFinite(id) && id > 0 && String(id) === last ? id : null;
  }

  /** Fetch the context. Without `force`, only one call is ever made. */
  async load(force = false): Promise<AiContext | null> {
    if (this.inFlight && !force) return this.inFlight;

    this.inFlight = (async () => {
      try {
        const headers = await this.authHeaders();
        const pid = this.currentProjectId();
        const url = this.apiLink + 'context' + (pid ? '?project_id=' + pid : '');
        const res: any = await firstValueFrom(this.http.get<any>(url, { headers }));
        const ctx: AiContext = res && res.status === 1 ? res.data : { allowed: false, code: 'ERROR' };
        this.context$.next(ctx);
        return ctx;
      } catch {
        // Assistant ka na chalna kabhi baaki app ko na rokey.
        const ctx: AiContext = { allowed: false, code: 'ERROR' };
        this.context$.next(ctx);
        return ctx;
      } finally {
        this.inFlight = null;
      }
    })();

    return this.inFlight;
  }

  /** Org ke admin ne haan boli. */
  async accept(): Promise<boolean> {
    const headers = await this.authHeaders();
    const res: any = await firstValueFrom(
      this.http.post<any>(this.apiLink + 'accept', {}, { headers }));
    if (res && res.status === 1) { await this.load(true); return true; }
    return false;
  }

  /**
   * One turn. The model is called — but NOTHING is written here.
   * If the model suggests an action, its proposal comes back, and the writing
   * happens in `apply()`, after a human presses the button.
   */
  async message(text: string, projectId: number | null): Promise<any> {
    const headers = await this.authHeaders();
    const res: any = await firstValueFrom(this.http.post<any>(
      this.apiLink + 'message', { message: text, project_id: projectId }, { headers }));
    return res;
  }

  /** The only place where anything is written on the assistant's say-so. */
  async apply(action: string, args: any, projectId: number | null): Promise<any> {
    const headers = await this.authHeaders();
    const res: any = await firstValueFrom(this.http.post<any>(
      this.apiLink + 'apply', { action, args, project_id: projectId }, { headers }));
    if (res && res.status === 1 && res.data && res.data.state) {
      // The state comes back with the response — no need to ask again.
      this.context$.next(Object.assign({}, this.context$.value, {
        project: res.data.state.project,
        actions: res.data.state.actions,
        chips: res.data.state.next,
        unblock: res.data.state.unblock,
        diagnosis: res.data.state.diagnosis,
        meta: res.data.state.meta,
      }) as AiContext);
    }
    return res;
  }

  /** The answer to "how does your org work". */
  async answer(key: string, value: string, projectId: number | null): Promise<any> {
    const headers = await this.authHeaders();
    const res: any = await firstValueFrom(this.http.post<any>(
      this.apiLink + 'answer', { key, value, project_id: projectId }, { headers }));
    if (res && res.status === 1) await this.load(true);
    return res;
  }

  /**
   * Locations — dhoondho ya ped me ghoomo.
   *
   * Browse matters because an admin often does not know the name; they need to
   * see what they have. And picking from a list removes both the guessing at
   * names and the "two places called Nashik" dead end.
   */
  async locations(opts: { q?: string; parent?: number | null }): Promise<{ locations: any[]; path: any[] }> {
    const headers = await this.authHeaders();
    const qs = opts.q ? 'q=' + encodeURIComponent(opts.q)
                      : (opts.parent ? 'parent=' + opts.parent : '');
    const res: any = await firstValueFrom(
      this.http.get<any>(this.apiLink + 'locations' + (qs ? '?' + qs : ''), { headers }));
    return res && res.status === 1
      ? { locations: res.data.locations || [], path: res.data.path || [] }
      : { locations: [], path: [] };
  }

  /** What is left on each project — for the "project design" chip. */
  async projects(): Promise<any[]> {
    const headers = await this.authHeaders();
    const res: any = await firstValueFrom(
      this.http.get<any>(this.apiLink + 'projects', { headers }));
    return res && res.status === 1 ? res.data.projects : [];
  }
}
